import { isTimePermissionEnabled } from '../config/applicationFeatures';
import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { AppButton } from './AppButton';
import { AppCard } from './AppCard';
import { ResponsiveGrid } from './ResponsiveGrid';
import { REPORT_ACCESS_PERMISSIONS } from '../config/reportCapabilities';
import { PROFILE_CAPABILITIES } from '../config/profileCapabilities';
import { SETTINGS_CAPABILITIES } from '../config/settingsCapabilities';
import { PERMISSIONS, WORKFLOW_INBOX_PERMISSIONS } from '../constants/permissions';
import { routes, type RootStackParamList } from '../constants/routes';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAuth } from '../context/AuthContext';
import {
  formatUnreadBadge,
  useNotificationCount,
} from '../context/NotificationCountContext';
import { usePendingRequestCount } from '../context/PendingRequestCountContext';
import { useAppColors } from '../context/PreferencesContext';
import { navigateRoot } from '../navigation/navigationRef';
import { confirmLogout } from '../utils/confirmLogout';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';

type Props = {
  /** Called after a menu destination is chosen (e.g. close drawer). */
  onNavigate?: () => void;
};

export function MenuPanel({ onNavigate }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const notificationCount = useNotificationCount();
  const pendingRequests = usePendingRequestCount();
  const [error, setError] = useState('');
  const { isCompact } = useResponsiveLayout();
  const unreadBadge = formatUnreadBadge(notificationCount.unreadCount);
  const leaveBadge = formatUnreadBadge(pendingRequests.leaveCount);
  const wfhBadge = formatUnreadBadge(pendingRequests.wfhCount);
  const permissionBadge = formatUnreadBadge(pendingRequests.permissionCount);
  const correctionBadge = formatUnreadBadge(
    pendingRequests.attendanceCorrectionCount,
  );
  const approvalBadge = formatUnreadBadge(pendingRequests.approvalCount);
  const isAdmin = auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW);

  const themed = useMemo(
    () => ({
      item: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '600' as const,
        flex: 1,
        marginRight: spacing.sm,
      },
      comingSoon: { ...typography.caption, color: palette.textMuted },
      badge: { backgroundColor: palette.primary },
      badgeText: {
        ...typography.caption,
        color: palette.onPrimary,
        fontWeight: '700' as const,
      },
      error: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  function go(route: keyof RootStackParamList, params?: object) {
    onNavigate?.();
    navigateRoot(route, params);
  }

  async function logout() {
    setError('');
    try {
      onNavigate?.();
      await auth.signOut();
    } catch {
      setError('Signed out locally, but the server could not be reached.');
    }
  }

  return (
    <View style={styles.body}>
      <ResponsiveGrid columns={isCompact ? 1 : 2} style={styles.grid}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="My Profile"
        onPress={() => go(routes.profile)}
      >
        <AppCard style={styles.card}>
          <Text style={themed.item}>My Profile</Text>
        </AppCard>
      </Pressable>
      {SETTINGS_CAPABILITIES.themeLocal ||
      SETTINGS_CAPABILITIES.notificationPreferences ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Settings"
          onPress={() => go(routes.settings)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Settings</Text>
          </AppCard>
        </Pressable>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="About"
        onPress={() => go(routes.about)}
      >
        <AppCard style={styles.card}>
          <Text style={themed.item}>About</Text>
        </AppCard>
      </Pressable>
      {PROFILE_CAPABILITIES.changePassword ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Change Password"
          onPress={() => go(routes.changePassword)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Change Password</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.FACE_REGISTER) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Face Admin"
          onPress={() => go(routes.faceAdmin)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Face Admin</Text>
            <Text style={themed.comingSoon}>
              Providers, person group, sync & cleanup
            </Text>
          </AppCard>
        </Pressable>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          unreadBadge
            ? `Notifications, ${unreadBadge} unread`
            : 'Notifications'
        }
        onPress={() => go(routes.notificationList)}
      >
        <AppCard style={styles.card}>
          <Text style={themed.item}>Notifications</Text>
          {unreadBadge ? (
            <View
              style={[styles.badge, themed.badge]}
              accessibilityLabel={`${unreadBadge} unread notifications`}
            >
              <Text style={themed.badgeText}>{unreadBadge}</Text>
            </View>
          ) : (
            <Text style={themed.comingSoon}>Unread count & preferences</Text>
          )}
        </AppCard>
      </Pressable>
      {auth.hasPermission(PERMISSIONS.LEAVE_APPLY) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="My Leave"
          onPress={() => go(routes.mainTabs, { screen: routes.leave })}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>My Leave</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.WFH_APPLY) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Work From Home"
          onPress={() => go(routes.wfhHome)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Work From Home</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {isTimePermissionEnabled() && auth.hasPermission(PERMISSIONS.TIME_PERMISSION_VIEW) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="My Permission Requests"
          onPress={() => go(routes.timePermissionHome)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>My Permission Requests</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.ATTENDANCE_CORRECT) && !isAdmin ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Attendance Correction"
          onPress={() => go(routes.attendanceCorrectionHome)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Attendance Correction</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.LEAVE_APPROVE) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            leaveBadge
              ? `${
                  auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                    ? 'Team Leave Requests'
                    : 'Leave Requests'
                }, ${leaveBadge} pending`
              : auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                ? 'Team Leave Requests'
                : 'Leave Requests'
          }
          onPress={() => go(routes.adminLeaveRequests)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>
              {auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                ? 'Team Leave Requests'
                : 'Leave Requests'}
            </Text>
            {leaveBadge ? (
              <View
                style={[styles.badge, themed.badge]}
                accessibilityLabel={`${leaveBadge} pending leave requests`}
              >
                <Text style={themed.badgeText}>{leaveBadge}</Text>
              </View>
            ) : null}
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.WFH_APPROVE) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            wfhBadge
              ? `${
                  auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                    ? 'Team WFH Requests'
                    : 'WFH Requests'
                }, ${wfhBadge} pending`
              : auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                ? 'Team WFH Requests'
                : 'WFH Requests'
          }
          onPress={() => go(routes.adminWfhRequests)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>
              {auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                ? 'Team WFH Requests'
                : 'WFH Requests'}
            </Text>
            {wfhBadge ? (
              <View
                style={[styles.badge, themed.badge]}
                accessibilityLabel={`${wfhBadge} pending WFH requests`}
              >
                <Text style={themed.badgeText}>{wfhBadge}</Text>
              </View>
            ) : null}
          </AppCard>
        </Pressable>
      ) : null}
      {isTimePermissionEnabled() && auth.hasPermission(PERMISSIONS.PERMISSION_APPROVE) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            permissionBadge
              ? `Team Permission Requests, ${permissionBadge} pending`
              : 'Team Permission Requests'
          }
          onPress={() => go(routes.adminTimePermissionRequests)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Team Permission Requests</Text>
            {permissionBadge ? (
              <View
                style={[styles.badge, themed.badge]}
                accessibilityLabel={`${permissionBadge} pending permission requests`}
              >
                <Text style={themed.badgeText}>{permissionBadge}</Text>
              </View>
            ) : null}
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.ATTENDANCE_APPROVE) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            correctionBadge
              ? `${
                  auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                    ? 'Team Attendance Corrections'
                    : 'Attendance Corrections'
                }, ${correctionBadge} pending`
              : auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                ? 'Team Attendance Corrections'
                : 'Attendance Corrections'
          }
          onPress={() => go(routes.adminAttendanceCorrectionRequests)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>
              {auth.hasRole('TEAM_LEAD') && !auth.hasRole('HR_ADMIN')
                ? 'Team Attendance Corrections'
                : 'Attendance Corrections'}
            </Text>
            {correctionBadge ? (
              <View
                style={[styles.badge, themed.badge]}
                accessibilityLabel={`${correctionBadge} pending attendance correction requests`}
              >
                <Text style={themed.badgeText}>{correctionBadge}</Text>
              </View>
            ) : null}
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasAnyPermission([...WORKFLOW_INBOX_PERMISSIONS]) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            approvalBadge ? `Approvals, ${approvalBadge} pending` : 'Approvals'
          }
          onPress={() => go(routes.workflowInbox)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Approvals</Text>
            {approvalBadge ? (
              <View
                style={[styles.badge, themed.badge]}
                accessibilityLabel={`${approvalBadge} pending approvals`}
              >
                <Text style={themed.badgeText}>{approvalBadge}</Text>
              </View>
            ) : null}
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasAnyPermission([...REPORT_ACCESS_PERMISSIONS]) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Reports"
          onPress={() => go(routes.reportList)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Reports</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.PROJECT_VIEW) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Projects"
          onPress={() => go(routes.projectList)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Projects</Text>
            <Text style={themed.comingSoon}>Members, roles & task columns</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.VISITOR_VIEW) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Visitor"
          onPress={() => go(routes.visitorList)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Visitor</Text>
            <Text style={themed.comingSoon}>Register, check-in & history</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasAnyPermission([
        PERMISSIONS.CALENDAR_VIEW,
        PERMISSIONS.ATTENDANCE_VIEW,
      ]) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Office Calendar"
          onPress={() => go(routes.officeCalendar)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Office Calendar</Text>
            <Text style={themed.comingSoon}>Holidays & working days</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.CALENDAR_MANAGE) ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Holiday calendars"
          onPress={() => go(routes.holidayCalendarList)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Holiday calendars</Text>
            <Text style={themed.comingSoon}>
              Create calendars and assign employees
            </Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.roles?.includes('SUPER_ADMIN') ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Organizations"
          onPress={() => go(routes.organizationList)}
        >
          <AppCard style={styles.card}>
            <Text style={themed.item}>Organizations</Text>
          </AppCard>
        </Pressable>
      ) : null}
      {auth.hasPermission(PERMISSIONS.ORGANIZATION_VIEW) ? (
        <>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Organization"
            onPress={() => go(routes.organizationHome)}
          >
            <AppCard style={styles.card}>
              <Text style={themed.item}>Organization</Text>
            </AppCard>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Locations"
            onPress={() => go(routes.locationList)}
          >
            <AppCard style={styles.card}>
              <Text style={themed.item}>Locations</Text>
            </AppCard>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Departments"
            onPress={() => go(routes.departmentList)}
          >
            <AppCard style={styles.card}>
              <Text style={themed.item}>Departments</Text>
            </AppCard>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Designations"
            onPress={() => go(routes.designationList)}
          >
            <AppCard style={styles.card}>
              <Text style={themed.item}>Designations</Text>
            </AppCard>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Teams"
            onPress={() => go(routes.teamList)}
          >
            <AppCard style={styles.card}>
              <Text style={themed.item}>Teams</Text>
            </AppCard>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Shifts"
            onPress={() => go(routes.shiftList)}
          >
            <AppCard style={styles.card}>
              <Text style={themed.item}>Shifts</Text>
            </AppCard>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Work Policies"
            onPress={() => go(routes.workPolicyList)}
          >
            <AppCard style={styles.card}>
              <Text style={themed.item}>Work Policies</Text>
            </AppCard>
          </Pressable>
          {auth.hasRole('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="WFH Configuration"
              onPress={() => go(routes.wfhConfigurationList)}
            >
              <AppCard style={styles.card}>
                <Text style={themed.item}>WFH Configuration</Text>
              </AppCard>
            </Pressable>
          ) : null}
          {auth.hasRole('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Late Arrival Configuration"
              onPress={() => go(routes.lateArrivalConfiguration)}
            >
              <AppCard style={styles.card}>
                <Text style={themed.item}>Late Arrival Configuration</Text>
              </AppCard>
            </Pressable>
          ) : null}
          {isTimePermissionEnabled() && auth.hasRole('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
            <Pressable accessibilityRole="button" accessibilityLabel="Permission Configuration" onPress={() => go(routes.permissionConfigurationList)}>
              <AppCard style={styles.card}><Text style={themed.item}>Permission Configuration</Text></AppCard>
            </Pressable>
          ) : null}
          {auth.hasRole('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Leave Configuration"
              onPress={() => go(routes.leaveConfigurationList)}
            >
              <AppCard style={styles.card}>
                <Text style={themed.item}>Leave Configuration</Text>
              </AppCard>
            </Pressable>
          ) : null}
          {auth.hasRole('HR_ADMIN') && auth.hasPermission(PERMISSIONS.SETTINGS_VIEW) ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Leave Balances"
              onPress={() => go(routes.leaveBalanceList)}
            >
              <AppCard style={styles.card}>
                <Text style={themed.item}>Leave Balances</Text>
              </AppCard>
            </Pressable>
          ) : null}
        </>
      ) : null}
      </ResponsiveGrid>
      {error ? (
        <Text accessibilityRole="alert" style={themed.error}>
          {error}
        </Text>
      ) : null}
      {PROFILE_CAPABILITIES.logout ? (
        <AppButton
          title="Logout"
          variant="danger"
          onPress={() => confirmLogout({ onConfirm: logout })}
          style={styles.logout}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  body: { gap: 0, width: '100%', minWidth: 0 },
  grid: { rowGap: 0, width: '100%' },
  card: {
    marginBottom: spacing.md,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  logout: { marginTop: spacing.sm, marginBottom: spacing.xl },
  badge: {
    minWidth: 28,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 999,
    alignItems: 'center',
  },
});
